<script setup>
import {onMounted, ref, watch} from "vue";
import {useStore} from "vuex";
import Swal from 'sweetalert2';
import DefaultButton from "@/components/onsiteEditor/components/buttons/DefaultButton.vue";
import TipBox from "@/components/onsiteEditor/components/TipBox.vue";
import BaseModal from "@/components/global/modals/BaseModal.vue";

const props = defineProps({
  userUsingOptimiser: {
    type: [Object, null],
    required: false,
    default: null,
  },
  shouldShowHelpVideo: {
    type: Boolean,
    required: true,
  },
});

const store = useStore();

const randomTip = ref(null);
const dontShowIsChecked = ref(false);
const showLearnMore = ref(false);

watch(showLearnMore, async (newValue) => {
  if (newValue) {
    await logMixpanelVideoWatched();
  }
});

const logMixpanelVideoWatched = async () => {
  await window.mixpanel('Optimizer: 60 Second Video');
}

onMounted(async () => {
  await showOptimiserInUseWarning();

  randomTip.value = await store.dispatch('onsiteEditorInstallation/fetchSingleRandomTip');
});

const handleVideoEnded = async() => {
  await store.dispatch('onsiteEditorInstallation/completedHelpVideo');

  await openInNewTab(true);

  window.location.reload();
}

const showOptimiserInUseWarning = async () => {
  if (props.userUsingOptimiser !== null) {
    return Swal.fire({
      title: `Onsite Optimizer in use!`,
      icon: 'warning',
      html: `
        <strong>${props.userUsingOptimiser.name}</strong> has an active session on this website.
        <br><br> Would you like to end their session and take over?
        <br><br> <small>This will stop the user from being able to make any more changes until they relaunch Onsite Optimizer from the platform</small>`,
      reverseButtons: true,
      showCancelButton: true,
      confirmButtonText: 'Take over',
      confirmButtonColor: '#d33',
      showLoaderOnConfirm: true,
      backdrop: true,
      preConfirm: () => {
        return window.open('/onsite-optimizer/go-to-editor', '_blank');
      }
    });
  }
}

const openInNewTab = async (skipHelpVideo = false) => {
  if (props.shouldShowHelpVideo && !skipHelpVideo) {
    showLearnMore.value = true;
    return;
  }

  if (dontShowIsChecked.value) {
    await store.dispatch('onsiteEditorInstallation/hideSplashPage');
  }

  const response = await showOptimiserInUseWarning();

  if (response && response.isDismissed) {
    return;
  }

  window.open('/onsite-optimizer/go-to-editor', '_blank');
};
const toggleCheckbox = () => dontShowIsChecked.value = !dontShowIsChecked.value;
</script>

<template>
  <div class="tailwind">
    <div class="tw-xl-justify tw-mx-auto tw-mt-16 tw-flex tw-max-w-8xl tw-gap-16">
      <div class="tw-mt-2 tw-flex tw-basis-1/2">
        <SvgVue
          class="!tw-h-[690px]"
          icon="illustrations/optimizer-splash"
        />
      </div>
      <div class="tw-flex tw-basis-1/2 tw-flex-col">
        <div class="tw-flex tw-flex-col tw-gap-8 tw-align-top">
          <div class="tw-text-xl tw-font-bold tw-leading-6.5 tw-text-grey-darkest">
            ONSITE OPTIMIZER
          </div>
          <div class="tw-text-5xl tw-font-bold tw-leading-[67px] tw-text-grey-darkest">
            Optimize Your Site with AI Using Onsite Optimizer
          </div>
          <div class="t-max-w-[645px] tw-text-2xl tw-leading-[1.8rem] tw-text-grey-semi-dark">
            You’re all set to tackle your SEO actions live, directly on your website, with the Onsite Optimizer and its in-built AI features.
          </div>
        </div>
        <div class="tw-mt-12 tw-flex tw-items-center tw-gap-8">
          <DefaultButton
            class="!tw-h-15.5 !tw-w-56 !tw-bg-blue !tw-py-4.5 tw-px-16 tw-text-xl"
            @click="openInNewTab()"
          >
            Launch Now
          </DefaultButton>
          <div class="tw-flex tw-items-center tw-gap-2">
            <SvgVue
              class="tw-h-6 tw-w-6"
              icon="icons/camera-outline"
            />
            <div
              class="tw-cursor-pointer tw-text-xl tw-font-normal tw-leading-6 tw-text-grey-darkest tw-underline"
              @click="showLearnMore = true"
            >
              Learn more in 60 seconds
              <BaseModal
                v-if="showLearnMore"
                @close="showLearnMore = false"
              >
                <template #heading>
                  Before you get started watch this video
                </template>
                <iframe
                  src="https://player.vimeo.com/video/929844758?h=4363ad5cae&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                  title="Vimeo video player"
                  frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowfullscreen
                  class="tw-h-[22.93213rem] tw-w-[40.625rem] tw-rounded-[8px]"
                />

                <template
                  v-if="shouldShowHelpVideo"
                  #footer
                >
                  <div class="tw-m-auto tw-flex">
                    <DefaultButton
                      class="!tw-bg-blue !tw-py-4.5 tw-px-16 tw-text-xl"
                      @click="handleVideoEnded"
                    >
                      I've watched the video, let's go
                    </DefaultButton>
                  </div>
                </template>
              </BaseModal>
            </div>
          </div>
        </div>
        <div class="tw-mt-4 tw-flex tw-items-center tw-gap-2">
          <label for="hide-page">
            <input
              id="hide-page"
              v-model="dontShowIsChecked"
              type="checkbox"
              class="tw-hidden"
              @click.prevent
            >
            <div
              class="tw-gap tw-flex tw-cursor-pointer tw-items-center tw-gap-4"
              @click="toggleCheckbox"
            >
              <div
                class="rounded tw-relative tw-h-7.5 tw-w-7.5 tw-border-3 tw-border-grey-light-mid"
              >
                <SvgVue
                  v-if="dontShowIsChecked"
                  class="tw-absolute tw-left-[6.5px] tw-top-[6.5px] tw-h-3.5 tw-w-3.5 tw-border-grey-light-mid"
                  icon="icons/check"
                />
              </div>
              <span class="tw-text-base tw-leading-[19px] tw-text-grey">Do not show this page again</span>
            </div>
          </label>
        </div>
        <TipBox
          :tip="randomTip"
          class="tw-mt-10"
        />
      </div>
    </div>
  </div>
</template>
